import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { CheckCircle2, Circle, ClipboardList } from 'lucide-react';

const CATEGORY_DOT = {
  'Vorbereitung Mittag': '#EAB308',
  'Küche': '#EF4444',
  'Service': '#3B82F6',
  'Abschluss abends': '#8B5CF6',
  'Allgemein': '#5C5C59',
};

const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function weekdayLabel(wds) {
  if (!wds || wds.length === 0 || wds.length >= 7) return '';
  return [...wds].sort((a, b) => a - b).map(d => WEEKDAY_SHORT[d]).join(', ');
}

// Wiederverwendbare Checklisten-Karte (Lesen + Abhaken). Wird im Dashboard und
// auf der Checklisten-Seite genutzt. Keine Admin-Verwaltung hier.
export function ChecklistCard({ date, title = 'Checkliste', compact = false, reloadKey = 0 }) {
  const [data, setData] = useState({ groups: [], total: 0, done: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/checklist?date=${date}`);
      setData(r.data || { groups: [], total: 0, done: 0 });
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const toggle = async (item) => {
    const next = !item.done;
    // optimistisch
    setData(prev => ({
      ...prev,
      done: prev.done + (next ? 1 : -1),
      groups: prev.groups.map(g => ({
        ...g,
        items: g.items.map(it => it.id === item.id ? { ...it, done: next } : it),
      })),
    }));
    try {
      await api.post('/checklist/toggle', { date, item_id: item.id, done: next });
    } catch {
      toast.error('Konnte nicht speichern');
      load();
    }
  };

  const pct = data.total > 0 ? Math.round((data.done / data.total) * 100) : 0;
  const allDone = data.total > 0 && data.done === data.total;

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-lg p-4" data-testid="checklist-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg text-[#1C1C19] flex items-center gap-2">
          <ClipboardList className="w-4 h-4" /> {title}
        </h2>
        <span className={`text-sm font-medium ${allDone ? 'text-[#22C55E]' : 'text-[#5C5C59]'}`} data-testid="checklist-progress-label">
          {data.done}/{data.total} erledigt
        </span>
      </div>

      {data.total > 0 && (
        <div className="h-1.5 bg-[#F0F0ED] rounded-full mb-4 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, background: allDone ? '#22C55E' : '#1C1C19' }}
            data-testid="checklist-progress-bar"
          />
        </div>
      )}

      {loading ? (
        <p className="text-sm text-[#9CA3AF] py-4 text-center">Wird geladen…</p>
      ) : data.total === 0 ? (
        <p className="text-sm text-[#9CA3AF] py-6 text-center" data-testid="checklist-empty">Keine Aufgaben für diesen Tag.</p>
      ) : (
        <div className={compact ? 'space-y-3 max-h-80 overflow-y-auto pr-1' : 'space-y-4'}>
          {data.groups.map(group => (
            <div key={group.category} data-testid={`checklist-group-${group.category}`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: CATEGORY_DOT[group.category] || '#5C5C59' }} />
                <span className="text-xs uppercase tracking-wider text-[#5C5C59] font-medium">{group.category}</span>
              </div>
              <div className="space-y-1">
                {group.items.map(item => (
                  <button
                    key={item.id}
                    onClick={() => toggle(item)}
                    data-testid={`checklist-item-${item.id}`}
                    className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-[#F9F9F7] text-left transition group"
                  >
                    {item.done
                      ? <CheckCircle2 className="w-5 h-5 text-[#22C55E] shrink-0" />
                      : <Circle className="w-5 h-5 text-[#D1D5DB] group-hover:text-[#9CA3AF] shrink-0" />}
                    <span className={`text-sm flex-1 ${item.done ? 'line-through text-[#9CA3AF]' : 'text-[#1C1C19]'}`}>
                      {item.title}
                    </span>
                    {weekdayLabel(item.weekdays) && (
                      <span className="text-[10px] text-[#5C5C59] bg-[#F0F0ED] px-1.5 py-0.5 rounded shrink-0" title="Nur an diesen Wochentagen">{weekdayLabel(item.weekdays)}</span>
                    )}
                    {item.kind === 'onetime' && (
                      <span className="text-[10px] text-[#B45309] bg-[#B45309]/10 px-1.5 py-0.5 rounded shrink-0">heute</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
